import { For, Show, createMemo } from "solid-js";
import type { Component } from "solid-js";
import { useRobotListQuery } from "../service";
import { useShellStore } from "@ui/state/shell";

const resolve = <T,>(input: T | (() => T)): T => (typeof input === "function" ? (input as () => T)() : input);

const formatBattery = (level: number | null): string => {
  if (level === null || Number.isNaN(level)) {
    return "-";
  }
  return `${Math.round(level)}%`;
};

const formatDateTime = (value: Date | null): string => (value ? value.toLocaleString() : "-");

export const RobotListView: Component = () => {
  const query = useRobotListQuery();
  const {
    state,
    actions: { selectRobot },
  } = useShellStore();

  const robots = createMemo(() => resolve(query.data) ?? []);
  const isLoading = () => resolve(query.isLoading);
  const isFetching = () => resolve(query.isFetching);

  return (
    <section class="chat-list-view">
      <header class="chat-list-header">
        <div>
          <h1 class="main-placeholder-title">Robot fleet</h1>
          <p>Monitor deployment status, battery levels, and assignments.</p>
        </div>
        <div class="chat-list-actions">
          <button
            type="button"
            class="button-primary refresh-button"
            onClick={() => void query.refetch()}
            disabled={isFetching()}
            title="Refresh"
          >
            <i class={`fa-solid fa-rotate-right ${isFetching() ? "spinning" : ""}`}></i>
          </button>
        </div>
      </header>

      <Show when={!isLoading()} fallback={<div class="placeholder-card">Loading robots...</div>}>
        <Show when={robots().length > 0} fallback={<div class="placeholder-card">No robots connected yet.</div>}>
          <table class="chat-table">
            <thead>
              <tr>
                <th>Device Name</th>
                <th class="mobile-hide">Manufacturer</th>
                <th>Status</th>
                <th>Battery</th>
                <th class="mobile-hide">Model</th>
                <th class="mobile-hide">Location</th>
                <th class="mobile-hide">Active</th>
              </tr>
            </thead>
            <tbody>
              <For each={robots()}>
                {(robot) => (
                  <tr
                    class={state.selectedRobotId === robot.id ? "is-selected" : ""}
                    onClick={() => selectRobot(robot.id)}
                  >
                    <td>
                      <div class="settings-device-name">
                        <Show when={robot.isActive}>
                          <span class="settings-status-dot online"></span>
                        </Show>
                        <Show when={!robot.isActive}>
                          <span class="settings-status-dot offline"></span>
                        </Show>
                        {robot.displayName || robot.model || robot.id}
                      </div>
                    </td>
                    <td class="mobile-hide">{robot.manufacturer ?? robot.provider ?? "Unknown"}</td>
                    <td>
                      <span class={`settings-status-badge ${robot.isActive ? "online" : "offline"}`}>
                        {robot.status || (robot.isActive ? "Online" : "Offline")}
                      </span>
                    </td>
                    <td>{formatBattery(robot.batteryLevel)}</td>
                    <td class="mobile-hide">{robot.model || <span class="settings-muted">Unknown</span>}</td>
                    <td class="mobile-hide">{robot.locationHint || robot.locationId || <span class="settings-muted">-</span>}</td>
                    <td class="mobile-hide">
                      <span class={`robot-active-badge ${robot.isActive ? "is-active" : "is-inactive"}`}>
                        {robot.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
};
